import {
  CategoryOutlined,
  ChecklistOutlined,
  FormatQuoteOutlined,
  WidgetsOutlined,
} from "@mui/icons-material";
import { Alert, Button } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from "react";
import ArtifactsTab from "src/reusable/Sidebar/tabs/ArtifactsTab/ArtifactsTab";
import SourcesTab from "src/reusable/Sidebar/tabs/SourcesTab/SourcesTab";
import type { SidebarConfig } from "src/reusable/Sidebar/types";
import type { ItemEquivalencyRecord } from "../services/ItemEquivalencyService";
import ItemEquivalencyPlanTab from "../components/ItemEquivalencyPlanTab";
import ItemEquivalencyPartsTab from "../components/ItemEquivalencyPartsTab";
import { useUIStore } from "src/views/assistant/stores/UIStore";

export interface ItemEquivalencySidebarContext {
  dialogueId?: number;
  record?: ItemEquivalencyRecord;
  // A failed record fetch must not look like "not loaded yet" — the Plan and
  // Parts tabs render an explicit error state instead of null.
  recordError: boolean;
  recordErrorDetail?: string;
  // Absent when the server rejected the request (4xx): repeating it cannot
  // succeed, so the error state offers no Retry.
  retryRecord?: () => void;
  responseSources: any[];
  artifacts: Record<string, any>;
  viewMode: "dataChunk" | "cardList";
  setViewMode: Dispatch<SetStateAction<"dataChunk" | "cardList">>;
}

const RecordLoadError = ({
  detail,
  onRetry,
}: {
  detail?: string;
  onRetry?: () => void;
}) => (
  <Alert
    severity="error"
    sx={{ m: 2 }}
    data-testid="item-equivalency-sidebar-record-error"
    action={
      onRetry && (
        <Button
          color="inherit"
          size="small"
          onClick={onRetry}
          data-testid="item-equivalency-sidebar-retry"
        >
          Retry
        </Button>
      )
    }
  >
    Could not load the evaluation record.
    {detail ? ` ${detail}` : ""}
  </Alert>
);

export const useItemEquivalencySidebar = (
  context: ItemEquivalencySidebarContext,
): SidebarConfig<ItemEquivalencySidebarContext> => {
  const { preferences, setRightSidebar } = useUIStore();
  const autoOpenEnabled = preferences?.autoOpenSidebar ?? true;
  const hasRecord = context.record != null;

  // When the record first appears (and auto-open is on), open the sidebar on
  // the plan tab. Width is left alone so the user's last-dragged width (or the
  // 400px default) applies — the old comparison sidebar behaved this way, and
  // forcing half the window here would bypass the resize clamp (maxWidth 800).
  const prevHasRecord = useRef(false);
  useEffect(() => {
    if (hasRecord && !prevHasRecord.current && autoOpenEnabled) {
      setRightSidebar({
        isOpen: true,
        selectedItem: "plan",
      });
    }
    prevHasRecord.current = hasRecord;
  }, [hasRecord, autoOpenEnabled, setRightSidebar]);

  return useMemo(
    () => ({
      tabs: [
        {
          id: "plan",
          label: "Plan",
          icon: ChecklistOutlined,
          renderContent: ({ context: current }) =>
            current?.recordError ? (
              <RecordLoadError
                detail={current.recordErrorDetail}
                onRetry={current.retryRecord}
              />
            ) : current?.record ? (
              <ItemEquivalencyPlanTab record={current.record} />
            ) : null,
          getBadgeContent: (current) =>
            current?.record?.stages.filter(
              (stage) => stage.status === "complete",
            ).length ?? 0,
        },
        {
          id: "parts",
          label: "Parts",
          icon: WidgetsOutlined,
          renderContent: ({ context: current }) =>
            current?.recordError ? (
              <RecordLoadError
                detail={current.recordErrorDetail}
                onRetry={current.retryRecord}
              />
            ) : current?.record ? (
              <ItemEquivalencyPartsTab record={current.record} />
            ) : null,
          getBadgeContent: (current) =>
            current?.record
              ? [
                  current.record.original_part_snapshot,
                  current.record.replacement_part_snapshot,
                ].filter(Boolean).length
              : 0,
        },
        {
          id: "sources",
          label: "Sources",
          icon: FormatQuoteOutlined,
          renderContent: ({ context: current }) =>
            current ? (
              <SourcesTab
                sources={current.responseSources}
                viewMode={current.viewMode}
                setViewMode={current.setViewMode}
                dialogueId={current.dialogueId ?? null}
                detailSourceViewSdrId={null}
                detailSourceViewSourceId={null}
                setDetailSourceViewSdrId={() => {}}
                setDetailSourceViewSourceId={() => {}}
              />
            ) : null,
          getBadgeContent: (current) => current?.responseSources.length ?? 0,
        },
        {
          id: "artifacts",
          label: "Artifacts",
          icon: CategoryOutlined,
          renderContent: ({ context: current }) =>
            current ? <ArtifactsTab artifacts={current.artifacts} /> : null,
          getBadgeContent: (current) =>
            current?.artifacts ? Object.keys(current.artifacts).length : 0,
        },
      ],
      defaultTab: "plan",
      responsive: {
        enableMobile: true,
        enableDesktopNav: true,
        enableResize: true,
        minWidth: 300,
        maxWidth: 800,
        defaultWidth: 400,
      },
    }),
    // The config's shape is static. renderContent/getBadgeContent receive
    // the LIVE context as their call-time parameter (UnifiedSidebar's
    // calling convention), so nothing here goes stale — do not add context
    // fields as deps, and do not read the outer `context` inside them.
    [],
  );
};
